import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [tempToken, setTempToken] = useState(null);
  const [emailHint, setEmailHint] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [rememberMe, setRememberMeState] = useState(false);

  // Rehydrate on mount
  useEffect(() => {
    const stored =
      localStorage.getItem('rcq_access_token') ||
      sessionStorage.getItem('rcq_access_token');
    const storedUser =
      localStorage.getItem('rcq_user') ||
      sessionStorage.getItem('rcq_user');
    if (stored && storedUser) {
      try {
        setAccessToken(stored);
        setUserSummary(JSON.parse(storedUser));
      } catch (_) {}
    }
  }, []);

  const loginWithTemp = (token, user, remember, hint) => {
    setTempToken(token);
    setUserSummary(user);
    setRememberMeState(!!remember);
    setEmailHint(hint || null);
  };

  const completeLogin = (token, user, remember) => {
    setAccessToken(token);
    setUserSummary(user);
    setTempToken(null);
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem('rcq_access_token', token);
    storage.setItem('rcq_user', JSON.stringify(user));
    // Bridge to existing RetailCLIQ ERP MainApp axios layer (reads `retailcliq_token`)
    // and the help-engine FormHelp wrappers that read `retailcliq_user` for userRole.
    try {
      const legacyUser = {
        role: user?.role || '',
        username: user?.name || '',
        userId: user?.client_id || user?.slno || '',
        email: user?.email || '',
        groupId: user?.role === 'SUPER_ADMIN' ? 'MAdmin' : '',
      };
      // Honour the remember-me flag for the TOKEN only.
      // Profile data (role/username) is always written to localStorage so all
      // screens that read localStorage.getItem('retailcliq_user') work regardless
      // of the remember-me setting.
      localStorage.setItem('retailcliq_user', JSON.stringify(legacyUser));
      if (remember) {
        localStorage.setItem('retailcliq_token', token);
      } else {
        sessionStorage.setItem('retailcliq_token', token);
        sessionStorage.setItem('retailcliq_user', JSON.stringify(legacyUser));
      }
    } catch (_) { /* silent */ }
  };

  const logout = () => {
    setTempToken(null);
    setAccessToken(null);
    setUserSummary(null);
    [
      'rcq_access_token', 'rcq_user', 'rcq_global_context',
      'retailcliq_token', 'retailcliq_user', 'retailcliq_global_context',
    ].forEach(k => {
      try { localStorage.removeItem(k); sessionStorage.removeItem(k); } catch (_) {}
    });
  };

  return (
    <AuthContext.Provider value={{
      tempToken, emailHint, accessToken, userSummary, rememberMe,
      loginWithTemp, completeLogin, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
